/**
 * Gestión de Scouts - Panel de Administración
 *
 * Permite a los administradores:
 * - Ver todos los scouts de su organización
 * - Crear, editar y desactivar scouts
 * - Ver cuántos futbolistas ha captado cada scout
 */

import { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import UnifiedDataTable from '../../components/admin/UnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import { toast } from 'react-toastify';
import ScoutService from '../../services/scoutService';
import organizacionService from '../../services/organizacionService';
import postgresUserService from '../../services/postgresUserService';
import * as XLSX from 'xlsx';
import './TransferManagement.css'; // Reutilizar estilos

const ScoutManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Estados principales
  const [scouts, setScouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizacion, setOrganizacion] = useState(null);

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCaptadosModal, setShowCaptadosModal] = useState(false);
  const [selectedScout, setSelectedScout] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Formulario
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    notas: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    if (user && user.id) {
      loadOrganizacionYScouts();
    }
  }, [user]);

  const loadOrganizacionYScouts = async () => {
    setLoading(true);
    try {
      // 1. Buscar usuario PostgreSQL
      const usuariosRes = await postgresUserService.getUsuarios();
      const usuarioPostgres = (usuariosRes.data || []).find(u => u.email === user.email);

      if (!usuarioPostgres) {
        toast.error('Usuario no encontrado en PostgreSQL');
        setLoading(false);
        return;
      }

      // 2. Buscar organización del gestor
      const orgRes = await organizacionService.getOrganizacionPorGestor(usuarioPostgres.id);
      if (!orgRes.success) {
        toast.error('No se encontró organización asociada');
        setLoading(false);
        return;
      }

      setOrganizacion(orgRes.data);

      // 3. Cargar scouts de la organización
      await loadScouts(orgRes.data.id);
    } catch (err) {
      console.error('Error cargando datos:', err);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const loadScouts = async (orgId) => {
    try {
      const data = await ScoutService.getScouts(orgId || organizacion?.id);
      setScouts(data || []);
    } catch (err) {
      console.error('Error cargando scouts:', err);
      toast.error('Error al cargar scouts');
    }
  };

  // Handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      setSubmitting(true);
      await ScoutService.createScout({
        ...formData,
        organizacion_id: organizacion.id
      });
      toast.success('Scout creado exitosamente');
      setShowCreateModal(false);
      setFormData({ nombre: '', telefono: '', email: '', notas: '' });
      await loadScouts();
    } catch (err) {
      toast.error('Error al crear scout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      setSubmitting(true);
      await ScoutService.updateScout(selectedScout.id, formData);
      toast.success('Scout actualizado exitosamente');
      setShowEditModal(false);
      setSelectedScout(null);
      await loadScouts();
    } catch (err) {
      toast.error('Error al actualizar scout');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActivo = async (scout) => {
    try {
      await ScoutService.updateScout(scout.id, { activo: !scout.activo });
      toast.success(scout.activo ? 'Scout desactivado' : 'Scout activado');
      await loadScouts();
    } catch (err) {
      toast.error('Error al cambiar estado del scout');
    }
  };

  const openEditModal = (scout) => {
    setSelectedScout(scout);
    setFormData({
      nombre: scout.nombre || '',
      telefono: scout.telefono || '',
      email: scout.email || '',
      notas: scout.notas || ''
    });
    setShowEditModal(true);
  };

  const openCaptadosModal = async (scout) => {
    try {
      console.log('📋 Cargando captados para scout:', scout.id, scout.nombre);
      const data = await ScoutService.getScoutById(scout.id);
      console.log('📋 Datos recibidos:', data);
      setSelectedScout(data);
      setShowCaptadosModal(true);
    } catch (err) {
      console.error('❌ Error al cargar captados:', err);
      toast.error('Error al cargar futbolistas captados');
    }
  };

  const handleExportCaptados = (scout) => {
    if (!scout.futbolistas_captados?.length) {
      toast.warn('Este scout no tiene futbolistas captados');
      return;
    }

    const data = scout.futbolistas_captados.map(f => ({
      'Nombre Futbolista': f.nombre,
      'Año Nacimiento': f.ano_nacimiento || '',
      'Género': f.genero || '',
      'Sede': f.sede_nombre || '',
      'Estado': f.estado_label || 'Sin inscripción',
      'Torneo': f.torneo_nombre || '-',
      'Saldo Comprado': f.saldo_comprado || 0,
      'Saldo Restante': f.saldo_jornadas || 0,
      'Fecha Inscripción': f.fecha_inscripcion ? new Date(f.fecha_inscripcion).toLocaleDateString('es-ES') : '-',
      'Scout': scout.nombre
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Captados');
    XLSX.writeFile(wb, `Captados_${scout.nombre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  // Configuración de columnas
  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      render: (row) => <span style={{ fontWeight: 'bold' }}>{row.nombre}</span>
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      render: (row) => row.telefono || '-'
    },
    {
      key: 'email',
      label: 'Email',
      render: (row) => row.email || '-'
    },
    {
      key: 'notas',
      label: 'Notas',
      render: (row) => row.notas ? (
        <span title={row.notas} style={{ fontSize: '13px', color: '#666' }}>
          {row.notas.length > 30 ? row.notas.substring(0, 30) + '...' : row.notas}
        </span>
      ) : '-'
    },
    {
      key: 'total_captados',
      label: 'Captados',
      render: (row) => (
        <span
          onClick={(e) => { e.stopPropagation(); if (row.total_captados > 0) openCaptadosModal(row); }}
          style={{
            backgroundColor: row.total_captados > 0 ? '#e8f5e9' : '#f5f5f5',
            color: row.total_captados > 0 ? '#2e7d32' : '#999',
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: 'bold',
            fontSize: '14px',
            cursor: row.total_captados > 0 ? 'pointer' : 'default',
            textDecoration: row.total_captados > 0 ? 'underline' : 'none'
          }}
        >
          {row.total_captados}
        </span>
      )
    },
    {
      key: 'activo',
      label: 'Estado',
      render: (row) => (
        <span style={{
          backgroundColor: row.activo ? '#e8f5e9' : '#fce4ec',
          color: row.activo ? '#2e7d32' : '#c62828',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '13px'
        }}>
          {row.activo ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  const handleExportCaptadosFromRow = async (row) => {
    try {
      const data = await ScoutService.getScoutById(row.id);
      handleExportCaptados(data);
    } catch (err) {
      toast.error('Error al exportar captados');
    }
  };

  const actions = [
    {
      key: 'edit',
      icon: 'edit',
      label: 'Editar',
      type: 'primary',
      onClick: (row) => openEditModal(row)
    },
    {
      key: 'export',
      icon: 'file_download',
      label: 'Exportar captados',
      type: 'default',
      onClick: (row) => handleExportCaptadosFromRow(row),
      disabled: (row) => !row.total_captados
    },
    {
      key: 'toggle',
      icon: (row) => row.activo ? 'visibility_off' : 'visibility',
      label: (row) => row.activo ? 'Desactivar' : 'Activar',
      type: (row) => row.activo ? 'warning' : 'success',
      onClick: (row) => handleToggleActivo(row)
    }
  ];

  // Estadísticas
  const totalScouts = scouts.length;
  const scoutsActivos = scouts.filter(s => s.activo).length;
  const totalCaptados = scouts.reduce((acc, s) => acc + (s.total_captados || 0), 0);

  return (
    <AdminLayout
      title="Gestión de Scouts"
      subtitle={organizacion ? `Organización: ${organizacion.nombre}` : 'Cargando organización...'}
    >
      {/* Botón de navegación */}
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <Button variant="outline-secondary" onClick={() => navigate('/admin/matches')}>
          <FaArrowLeft className="me-2" />Volver a Torneos
        </Button>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant="primary" onClick={() => {
            setFormData({ nombre: '', telefono: '', email: '', notas: '' });
            setShowCreateModal(true);
          }}>
            <FaPlus className="me-2" />Nuevo Scout
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}>
            <span className="material-icons">people</span>
          </div>
          <div className="stat-info">
            <h3>Total Scouts</h3>
            <p className="stat-value">{totalScouts}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon completed">
            <span className="material-icons">check_circle</span>
          </div>
          <div className="stat-info">
            <h3>Activos</h3>
            <p className="stat-value">{scoutsActivos}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }}>
            <span className="material-icons">sports_soccer</span>
          </div>
          <div className="stat-info">
            <h3>Total Captados</h3>
            <p className="stat-value">{totalCaptados}</p>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="admin-card">
        <UnifiedDataTable
          columns={columns}
          data={scouts}
          actions={actions}
          loading={loading}
          emptyMessage="No hay scouts registrados. Crea el primero con el botón 'Nuevo Scout'."
          pagination={true}
          searchable={true}
        />
      </div>

      {/* Modal Crear Scout */}
      <StandardModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Nuevo Scout"
        size="medium"
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleCreate} disabled={submitting}>
              {submitting ? 'Creando...' : 'Crear Scout'}
            </Button>
          </div>
        }
      >
        <ScoutForm formData={formData} onChange={handleFormChange} />
      </StandardModal>

      {/* Modal Editar Scout */}
      <StandardModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Scout"
        size="medium"
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleUpdate} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        }
      >
        <ScoutForm formData={formData} onChange={handleFormChange} />
      </StandardModal>

      {/* Modal Futbolistas Captados */}
      <StandardModal
        isOpen={showCaptadosModal}
        onClose={() => { setShowCaptadosModal(false); setSelectedScout(null); }}
        title={`Futbolistas captados por: ${selectedScout?.nombre || ''}`}
        size="large"
        footer={
          selectedScout?.futbolistas_captados?.length > 0 ? (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => { setShowCaptadosModal(false); setSelectedScout(null); }}>Cerrar</Button>
              <Button variant="success" onClick={() => handleExportCaptados(selectedScout)}>
                📥 Exportar Excel
              </Button>
            </div>
          ) : null
        }
      >
        {selectedScout && (
          <div>
            {/* Resumen de estados */}
            {selectedScout.resumen && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                  ✅ Comprados: {selectedScout.resumen.comprados}
                </span>
                <span style={{ backgroundColor: '#fff3e0', color: '#f57c00', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                  ⏳ Inscritos sin compra: {selectedScout.resumen.inscritos_sin_compra}
                </span>
                <span style={{ backgroundColor: '#f5f5f5', color: '#999', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                  ❌ Sin inscripción: {selectedScout.resumen.sin_inscripcion}
                </span>
                {selectedScout.resumen.inactivos > 0 && (
                  <span style={{ backgroundColor: '#fce4ec', color: '#c62828', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}>
                    🚫 Inactivos: {selectedScout.resumen.inactivos}
                  </span>
                )}
              </div>
            )}

            {selectedScout.futbolistas_captados?.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Nombre</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Año Nac.</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Sede</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Estado</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>Torneo</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '13px', fontWeight: '600' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedScout.futbolistas_captados.map(f => (
                      <tr key={f.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px 12px', fontSize: '14px', fontWeight: '500' }}>{f.nombre}</td>
                        <td style={{ padding: '10px 12px', fontSize: '14px' }}>{f.ano_nacimiento || '-'}</td>
                        <td style={{ padding: '10px 12px', fontSize: '14px' }}>{f.sede_nombre || '-'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            backgroundColor: f.estado === 'comprado' ? '#e8f5e9' : f.estado === 'inscrito_sin_compra' ? '#fff3e0' : f.estado === 'inscripcion_inactiva' ? '#fce4ec' : '#f5f5f5',
                            color: f.estado_color || '#999',
                            padding: '3px 10px',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            {f.estado_label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>{f.torneo_nombre || '-'}</td>
                        <td style={{ padding: '10px 12px', fontSize: '14px', textAlign: 'center' }}>
                          {f.inscripcion_id ? (
                            <span title={`Comprado: ${f.saldo_comprado || 0} | Restante: ${f.saldo_jornadas || 0}`}>
                              {f.saldo_jornadas ?? 0}/{f.saldo_comprado ?? 0}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                Este scout aún no ha captado ningún futbolista
              </p>
            )}
          </div>
        )}
      </StandardModal>
    </AdminLayout>
  );
};

// Componente reutilizable para el formulario
const ScoutForm = ({ formData, onChange }) => (
  <div>
    <div className="form-group" style={{ marginBottom: '16px' }}>
      <label className="form-label" style={{ fontWeight: 'bold' }}>Nombre *</label>
      <input
        type="text"
        name="nombre"
        value={formData.nombre}
        onChange={onChange}
        placeholder="Nombre completo del scout"
        className="form-control"
        required
      />
    </div>
    <div className="form-group" style={{ marginBottom: '16px' }}>
      <label className="form-label" style={{ fontWeight: 'bold' }}>Teléfono</label>
      <input
        type="text"
        name="telefono"
        value={formData.telefono}
        onChange={onChange}
        placeholder="Ej: 612345678"
        className="form-control"
      />
    </div>
    <div className="form-group" style={{ marginBottom: '16px' }}>
      <label className="form-label" style={{ fontWeight: 'bold' }}>Email</label>
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={onChange}
        placeholder="scout@email.com"
        className="form-control"
      />
    </div>
    <div className="form-group" style={{ marginBottom: '0' }}>
      <label className="form-label" style={{ fontWeight: 'bold' }}>Notas</label>
      <textarea
        name="notas"
        value={formData.notas}
        onChange={onChange}
        placeholder="Notas adicionales sobre el scout..."
        className="form-control"
        rows={3}
      />
    </div>
  </div>
);

export default ScoutManagement;
